import React from 'react';
import {
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import Svg, { Image as SVGImage } from 'react-native-svg';
import { Image } from '@rneui/themed';
import { MainModal } from 'components/modal/Modal';
import DecisionDialog from 'components/Dialog/DecisionDialog';
import { addDataToInfoMap } from 'components/Dialog/infoMap';
import { type TableMapType, type TableType } from 'types/Table';
import { type ColumnInfoType } from 'types/ColumnInfo';
import { useTranslation } from 'react-i18next';
import { useLanguage } from 'context/LanguageContext';
import { ScreenSizeContext } from 'context/ScreenSizeContext';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  mapContainer: {
    flex: 1,
  },
  svg: {
    top: 0,
    left: 0,
  },
  mapImage: {
    // flex: 1,
    resizeMode: 'cover',
  },
  area: {
    position: 'absolute',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    padding: '1%',
    borderRadius: 5,
  },

  imageContent: {
    aspectRatio: 1,
    width: '100%',
    height: '100%',
    justifyContent: 'center',
  },
});

interface Props {
  tableMap: TableMapType;
  setFieldValue?: (data: TableType) => void;
}

const resolveLocalizedText = (
  value: unknown,
  currentLanguage: string
): string => {
  if (value == null) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'object') {
    const localizedValue = (value as Record<string, string>)[currentLanguage];
    if (localizedValue) {
      return localizedValue;
    }

    const fallback = (value as Record<string, string>)['en'];
    if (fallback) {
      return fallback;
    }

    const first = Object.values(value as Record<string, string>)[0];
    if (typeof first === 'string') {
      return first;
    }
  }

  return String(value);
};

const TableSelectMap: React.FC<Props> = ({ tableMap, setFieldValue }) => {
  const dimensions = React.useContext(ScreenSizeContext);
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const [isModalVisible, setIsModalVisible] = React.useState(false);
  const [table, setTable] = React.useState<TableType | null>(null);

  const tableColumnInfoMap = {
    name: {
      name: t('tableName'),
      type: 'text',
    },
    image: {
      name: 'Image',
      type: 'image',
    },
    capacity: {
      name: t('tableCapacity'),
      type: 'text',
    },
    description: {
      name: t('tableDescription'),
      type: 'text',
    },
    type_id: {
      name: 'Type ID',
      type: 'hidden',
    },
    id: {
      name: 'Table ID',
      type: 'hidden',
    },
  };

  const createPercentageStr = (value: number): string => {
    return `${(value * 100).toFixed(2)}%`;
  };

  const selectTable = (table: ColumnInfoType): void => {
    const value: TableType = {
      name: table.name.data as string,
      image: table.image.data as string,
      capacity: table.capacity.data as number,
      description: table.description.data as string,
      type_id: table.type_id.data as string,
      table_id: table.id.data as string,
    };

    if (setFieldValue !== undefined) setFieldValue(value);
    setIsModalVisible(false);
  };

  return (
    <View style={styles.container}>
      <MainModal
        isModalVisible={isModalVisible}
        setIsModalVisible={setIsModalVisible}
      >
        {table != null && (
          <DecisionDialog
            title={t('tableDetails')}
            message=""
            submitFunc={selectTable}
            handleFinalModal={() => {
              setIsModalVisible(false);
            }}
            data={addDataToInfoMap(table, tableColumnInfoMap)}
          />
        )}
      </MainModal>
      <View style={styles.mapContainer}>
        <Svg
          style={{ width: '100%', height: '100%' }}
          viewBox="0 0 330 330"
          preserveAspectRatio="xMidYMid meet"
        >
          <SVGImage
            width="100%"
            height="100%"
            href={
              tableMap?.image ?? require('../../../assets/not_found_image.jpg')
            }
            preserveAspectRatio="xMidYMid meet"
          />
        </Svg>
        {tableMap?.table_list.map((table: TableType) => (
          <TouchableOpacity
            key={table.id}
            testID={`table-touch-${table.id}`}
            onPress={() => {
              setTable(table);
              setIsModalVisible(true);
            }}
            style={[
              styles.area,
              {
                top: createPercentageStr(table.position_rate_y),
                left: createPercentageStr(table.position_rate_x),
                width: '15%',
              },
            ]}
          >
            <Text
              style={{
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: dimensions.window.width > 400 ? 8 : 5,
              }}
            >
              {resolveLocalizedText(table.name, currentLanguage)}
            </Text>
            <Image
              source={{
                uri: table.image,
              }}
              containerStyle={styles.imageContent}
              PlaceholderContent={<ActivityIndicator />}
            />
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );
};

export default TableSelectMap;
